import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Timeline,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  message,
  Spin,
  Alert,
} from 'antd'
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  CalendarOutlined,
  DollarOutlined,
  TruckOutlined,
} from '@ant-design/icons'
import { Subscription } from '@types'
import { subscriptionService } from '@services/subscriptionService'
import dayjs from 'dayjs'

const { Title, Text } = Typography
const { Option } = Select

// 使用Subscription类型，但扩展一些字段
interface SubscriptionDetailState extends Subscription {
  deliveries?: any[]
}

const SubscriptionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [form] = Form.useForm()

  const [subscription, setSubscription] = useState<SubscriptionDetailState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editModalVisible, setEditModalVisible] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)

  useEffect(() => {
    if (id) {
      loadSubscriptionDetail()
    }
  }, [id])

  const loadSubscriptionDetail = async () => {
    if (!id) return

    setLoading(true)
    try {
      const data = await subscriptionService.getSubscriptionDetail(id)
      setSubscription(data as SubscriptionDetailState)
      form.setFieldsValue({
        monthlyPrice: data.monthlyPrice,
        deliveryFrequency: data.deliveryFrequency,
        autoRenewal: data.autoRenewal,
        deliveryAddress: data.deliveryAddress,
      })
    } catch (error: any) {
      setError(error || '加载订阅详情失败')
    } finally {
      setLoading(false)
    }
  }

  const handleStatusUpdate = async (status: 'active' | 'expired' | 'cancelled') => {
    if (!id) return

    try {
      await subscriptionService.updateSubscriptionStatus(id, status)
      message.success('状态更新成功')
      loadSubscriptionDetail()
    } catch (error: any) {
      message.error(error || '状态更新失败')
    }
  }

  const handleUpdate = async (values: any) => {
    if (!id) return

    setUpdateLoading(true)
    try {
      await subscriptionService.updateSubscription(id, values)
      message.success('订阅信息更新成功')
      setEditModalVisible(false)
      loadSubscriptionDetail()
    } catch (error: any) {
      message.error(error || '更新失败')
    } finally {
      setUpdateLoading(false)
    }
  }

  const getStatusTag = (status: string) => {
    const statusMap = {
      active: { color: 'green', text: '进行中' },
      expired: { color: 'orange', text: '已过期' },
      cancelled: { color: 'red', text: '已取消' },
    }
    const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  const getPlanName = (planType: string) => {
    const planMap = {
      monthly4: '月付-配送4次/每周1次套餐',
      monthly8: '月付-配送8次/每周2次套餐',
      monthly12: '月付12次套餐',
      quarterly: '季付套餐',
      yearly: '年付套餐',
    }
    return planMap[planType as keyof typeof planMap] || planType
  }

  const deliveryColumns = [
    {
      title: '配送日期',
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '配送时间',
      dataIndex: 'deliveryTime',
      key: 'deliveryTime',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap = {
          pending: { color: 'orange', text: '待配送' },
          in_progress: { color: 'blue', text: '配送中' },
          completed: { color: 'green', text: '已完成' },
          cancelled: { color: 'red', text: '已取消' },
        }
        const config = statusMap[status as keyof typeof statusMap] || { color: 'default', text: status }
        return <Tag color={config.color}>{config.text}</Tag>
      },
    },
    {
      title: '配送员',
      dataIndex: 'deliveryman',
      key: 'deliveryman',
      render: (deliveryman: any) => deliveryman?.name || '未分配',
    },
    {
      title: '备注',
      dataIndex: 'note',
      key: 'note',
    },
  ]

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px' }}>加载中...</div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="加载失败"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={loadSubscriptionDetail}>
            重试
          </Button>
        }
      />
    )
  }

  if (!subscription) {
    return (
      <Alert
        message="订阅不存在"
        description="请检查订阅ID是否正确"
        type="warning"
        showIcon
      />
    )
  }

  return (
    <div>
      {/* 页面头部 */}
      <div style={{ marginBottom: '24px' }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/subscriptions')}
          style={{ marginBottom: '16px' }}
        >
          返回订阅列表
        </Button>
        <Title level={2} style={{ margin: 0 }}>
          订阅详情
        </Title>
        <Text type="secondary">
          订阅ID: {subscription.id}
        </Text>
      </div>

      {/* 操作按钮 */}
      <Card style={{ marginBottom: '24px' }}>
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditModalVisible(true)}
          >
            编辑订阅
          </Button>
          {subscription.status === 'active' && (
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleStatusUpdate('cancelled')}
            >
              取消订阅
            </Button>
          )}
          {subscription.status === 'expired' && (
            <Button
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusUpdate('active')}
            >
              激活订阅
            </Button>
          )}
          <Button icon={<PhoneOutlined />}>
            联系用户
          </Button>
          <Button icon={<PhoneOutlined />}>
            联系农户
          </Button>
        </Space>
      </Card>

      <Row gutter={[24, 24]}>
        {/* 基本信息 */}
        <Col xs={24} lg={12}>
          <Card title="订阅信息" style={{ marginBottom: '24px' }}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="订阅状态">
                {getStatusTag(subscription.status)}
              </Descriptions.Item>
              <Descriptions.Item label="套餐类型">
                {getPlanName(subscription.planType)}
              </Descriptions.Item>
              <Descriptions.Item label="月费">
                <Text strong style={{ color: '#cf1322' }}>
                  ¥{subscription.monthlyPrice.toFixed(2)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="订阅日期">
                {dayjs(subscription.subscriptionDate).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="到期日期">
                {dayjs(subscription.expiryDate).format('YYYY-MM-DD')}
              </Descriptions.Item>
              <Descriptions.Item label="自动续费">
                <Tag color={subscription.autoRenewal ? 'green' : 'default'}>
                  {subscription.autoRenewal ? '开启' : '关闭'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="配送频率">
                每月 {subscription.deliveryFrequency} 次
              </Descriptions.Item>
              <Descriptions.Item label="下次配送">
                {subscription.nextDeliveryDate ? 
                  dayjs(subscription.nextDeliveryDate).format('YYYY-MM-DD') : 
                  '待确定'
                }
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* 用户信息 */}
        <Col xs={24} lg={12}>
          <Card title="用户信息" style={{ marginBottom: '24px' }}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="用户昵称">
                {subscription.user.nickname}
              </Descriptions.Item>
              <Descriptions.Item label="联系电话">
                <Space>
                  <PhoneOutlined />
                  {subscription.user.phone}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="所在地区">
                {subscription.user.province} {subscription.user.city}
              </Descriptions.Item>
              <Descriptions.Item label="配送地址">
                {subscription.deliveryAddress || '未设置'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* 菜园信息 */}
        <Col xs={24} lg={12}>
          <Card title="菜园信息" style={{ marginBottom: '24px' }}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="菜园名称">
                {subscription.garden.name}
              </Descriptions.Item>
              <Descriptions.Item label="菜园描述">
                {subscription.garden.description}
              </Descriptions.Item>
              <Descriptions.Item label="菜园位置">
                <Space>
                  <HomeOutlined />
                  {subscription.garden.location}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="菜园面积">
                {subscription.garden.area} 平方米
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* 农户信息 */}
        <Col xs={24} lg={12}>
          <Card title="农户信息" style={{ marginBottom: '24px' }}>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="农户姓名">
                {subscription.garden.farmer.name}
              </Descriptions.Item>
              <Descriptions.Item label="联系电话">
                <Space>
                  <PhoneOutlined />
                  {subscription.garden.farmer.phone}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="邮箱">
                <Space>
                  <MailOutlined />
                  {subscription.garden.farmer.email || '未设置'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="地址">
                {subscription.garden.farmer.address || '未设置'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        {/* 统计数据 */}
        <Col xs={24}>
          <Card title="统计数据" style={{ marginBottom: '24px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="总配送次数"
                  value={subscription.totalDeliveries}
                  prefix={<TruckOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="总金额"
                  value={subscription.totalAmount}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="剩余天数"
                  value={dayjs(subscription.expiryDate).diff(dayjs(), 'day')}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="配送完成率"
                  value={subscription.totalDeliveries / (subscription.deliveryFrequency * 12) * 100}
                  precision={1}
                  suffix="%"
                  valueStyle={{ color: '#722ed1' }}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        {/* 配送历史 */}
        <Col xs={24}>
          <Card title="配送历史">
            <Table
              columns={deliveryColumns}
              dataSource={subscription.deliveries}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* 编辑模态框 */}
      <Modal
        title="编辑订阅信息"
        open={editModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setEditModalVisible(false)}
        confirmLoading={updateLoading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpdate}
        >
          <Form.Item
            label="月费"
            name="monthlyPrice"
            rules={[{ required: true, message: '请输入月费' }]}
          >
            <Input
              type="number"
              prefix="¥"
              placeholder="请输入月费"
            />
          </Form.Item>
          <Form.Item
            label="配送频率"
            name="deliveryFrequency"
            rules={[{ required: true, message: '请选择配送频率' }]}
          >
            <Select placeholder="请选择配送频率">
              <Option value={4}>每月4次</Option>
              <Option value={8}>每月8次</Option>
              <Option value={12}>每月12次</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="自动续费"
            name="autoRenewal"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="配送地址"
            name="deliveryAddress"
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入配送地址"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default SubscriptionDetailPage
